import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, X, Share2 } from "lucide-react";
import { useDocsAuth } from "../hooks/useDocsAuth";
import { ShareDocDialog } from "../components/ShareDocDialog";
import { getCategoryLabel, KNOWN_CATEGORY_ORDER, type DocsInventoryPage } from "../types";

export function DocsHomePage() {
  const { user, isLoading: authLoading } = useDocsAuth();
  const [pages, setPages] = useState<DocsInventoryPage[]>([]);
  const [allPages, setAllPages] = useState<DocsInventoryPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch docs — use search endpoint when query/tags active, inventory otherwise.
  // AbortController prevents stale responses from overwriting current results
  // (e.g. slow inventory fetch completing after a fast tag-filtered search).
  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    // eslint-disable-next-line -- standard fetch-in-effect loading pattern
    setLoading(true);
    setError("");

    const token = localStorage.getItem("docs_jwt");
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const hasSearch = debouncedSearch.trim().length > 0;
    const hasTags = activeTags.size > 0;

    let endpoint: string;
    if (hasSearch || hasTags) {
      const params = new URLSearchParams();
      if (hasSearch) params.set("q", debouncedSearch.trim());
      if (hasTags) params.set("tags", [...activeTags].join(","));
      endpoint = `/docs/search?${params}`;
    } else {
      endpoint = "/docs/inventory";
    }

    // Docs endpoints use their own JWT auth and don't follow the standard
    // ApiResponse envelope, so we use fetch directly instead of apiClient.
    fetch(`/api${endpoint}`, { headers, signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 503) {
            const body = await res.json().catch(() => null);
            throw new Error(
              body?.detail ??
                "Docs are temporarily unavailable. Please try again shortly.",
            );
          }
          throw new Error("Failed to load documents");
        }
        return res.json();
      })
      .then((data) => {
        if (!controller.signal.aborted) {
          setPages(data.pages);
          if (!hasSearch && !hasTags) {
            setAllPages(data.pages);
          }
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          if (err.name !== "AbortError") {
            setError(err.message || "Network error loading documents");
          }
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [authLoading, debouncedSearch, activeTags]);

  function toggleTag(tag: string) {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function clearFilters() {
    setSearch("");
    setDebouncedSearch("");
    setActiveTags(new Set());
  }

  // Derive tag order from full inventory (not filtered results) so filter buttons stay stable
  const tagOrder = useMemo(() => {
    const source = allPages.length > 0 ? allPages : pages;
    const allLabels = new Set(source.flatMap((p) => p.labels));
    const knownSet = new Set<string>(KNOWN_CATEGORY_ORDER);
    const newLabels = [...allLabels].filter((l) => !knownSet.has(l)).sort();
    return [...KNOWN_CATEGORY_ORDER.filter((c) => allLabels.has(c)), ...newLabels];
  }, [allPages, pages]);

  // Available tags: show all tags from the inventory for all users.
  // For scope=folder users, hide filter chips entirely — they can't browse
  // other folders, so offering filter chips is confusing (ADR-71391314).
  const availableTags = useMemo(() => {
    if (!user) return [];
    if (user.scope === "folder") return [];
    return tagOrder;
  }, [user, tagOrder]);

  // Group by labels — when filtered, deduplicate so each page appears once.
  // Client-side filter is applied as a safety net even when the API returns
  // filtered results, preventing stale inventory data from leaking through.
  const grouped = useMemo(() => {
    const groups: { label: string; displayLabel: string; items: DocsInventoryPage[] }[] = [];

    // Safety net: always filter client-side when tags are active
    const displayPages = activeTags.size > 0
      ? pages.filter((p) => p.labels.some((l) => activeTags.has(l)))
      : pages;

    const sortKey = (t: string) => t.replace(/^Explainer:\s*/i, "").toLowerCase();
    const sortItems = (items: DocsInventoryPage[]) =>
      [...items].sort((a, b) => sortKey(a.title).localeCompare(sortKey(b.title)));

    // For scope=folder users, render all pages as a single group labeled with
    // the scoped folder — not spread across multiple sections (which causes
    // duplication when a page has multiple labels, e.g. shared+ma).
    if (user?.scope === "folder" && user.folderId) {
      const items = sortItems(displayPages);
      if (items.length > 0) {
        groups.push({
          label: user.folderId,
          displayLabel: getCategoryLabel(user.folderId),
          items,
        });
      }
      return groups;
    }

    if (activeTags.size > 0) {
      for (const tag of tagOrder) {
        if (!activeTags.has(tag)) continue;
        const items = displayPages.filter((p) => p.labels.includes(tag));
        if (items.length > 0) {
          groups.push({
            label: tag,
            displayLabel: getCategoryLabel(tag),
            items: sortItems(items),
          });
        }
      }
    } else {
      for (const cat of tagOrder) {
        const items = displayPages.filter((p) => p.labels.includes(cat));
        if (items.length > 0) {
          groups.push({
            label: cat,
            displayLabel: getCategoryLabel(cat),
            items: sortItems(items),
          });
        }
      }

    }

    return groups;
  }, [pages, activeTags, user, tagOrder]);

  if (loading && pages.length === 0) {
    return (
      <div className="space-y-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-ev bg-ev-surface-subtle" />
        ))}
      </div>
    );
  }

  if (error && pages.length === 0) {
    return (
      <div className="rounded-ev border border-ev-danger-border bg-ev-danger-bg p-4 text-sm text-ev-danger-text">
        {error}
      </div>
    );
  }

  const customerName = user?.customerName;
  const hasActiveFilters = search.trim().length > 0 || activeTags.size > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ev-text-primary">Explainers</h1>
          {user && !user.isInternal && customerName && (
            <p className="mt-1 text-sm text-ev-text-secondary">
              Viewing as: <span className="font-medium">{customerName}</span>
            </p>
          )}
        </div>

        {/* Share library button (internal users only) */}
        {user?.isInternal && (
          <ShareDocDialog pageId="" pageTitle="Explainers Library">
            {(open) => (
              <button
                onClick={open}
                className="flex items-center gap-1.5 rounded-ev border border-ev-border bg-ev-surface px-3 py-2 text-sm font-medium text-ev-text-primary transition-colors hover:bg-ev-surface-subtle"
              >
                <Share2 className="h-4 w-4" />
                Share Library
              </button>
            )}
          </ShareDocDialog>
        )}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ev-text-muted" />
        <input
          type="text"
          placeholder="Search documents by keyword..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-ev border border-ev-border bg-ev-surface py-2 pl-9 pr-3 text-sm text-ev-text-primary placeholder:text-ev-text-muted focus:border-ev-info-text focus:outline-none focus:ring-1 focus:ring-ev-info-text"
        />
      </div>

      {/* Tag filter chips — internal users only (labels are internal metadata) */}
      {user?.isInternal && availableTags.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-ev-text-muted">Filter:</span>
          {availableTags.map((tag) => {
            const isActive = activeTags.has(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-ev-info-text text-white"
                    : "bg-ev-surface-subtle text-ev-text-secondary hover:bg-ev-border"
                }`}
              >
                {getCategoryLabel(tag)}
              </button>
            );
          })}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs text-ev-text-muted hover:text-ev-text-secondary"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
      )}

      {/* Results count */}
      <p className="text-xs text-ev-text-muted">
        {pages.length} document{pages.length !== 1 ? "s" : ""}
        {hasActiveFilters ? " matching" : " available"}
        {loading && <span className="ml-1 animate-pulse">...</span>}
      </p>

      {/* Grouped document cards */}
      {grouped.map((group) => (
        <section key={group.label}>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ev-text-secondary">
            {group.displayLabel}
            <span className="ml-2 text-xs font-normal text-ev-text-muted">
              ({group.items.length})
            </span>
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {group.items.map((page) => (
              <Link
                key={`${group.label}-${page.id}`}
                to={`/docs/page/${page.id}`}
                className="group flex items-start gap-3 rounded-ev border border-ev-border bg-ev-surface p-4 transition-colors hover:border-ev-info-text hover:bg-ev-surface-subtle"
              >
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-ev-text-muted group-hover:text-ev-info-text" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-ev-text-primary group-hover:text-ev-info-text">
                    {page.title}
                  </h3>
                  {user?.isInternal && (
                    <div className="mt-1.5 flex flex-wrap gap-1 print:hidden">
                      {page.labels.map((label) => (
                        <span
                          key={label}
                          className="inline-block rounded-full bg-ev-surface-subtle px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ev-text-muted"
                        >
                          {getCategoryLabel(label)}
                        </span>
                      ))}
                    </div>
                  )}
                  {page.last_updated && (
                    <p className="mt-1 text-xs text-ev-text-muted">
                      Updated {new Date(page.last_updated).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {user?.isInternal && (
                  <ShareDocDialog pageId={page.id} pageTitle={page.title}>
                    {(open) => (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(); }}
                        title="Share"
                        className="shrink-0 rounded p-1.5 text-ev-text-muted opacity-0 transition-all hover:bg-ev-surface hover:text-ev-info-text group-hover:opacity-100"
                      >
                        <Share2 className="h-4 w-4" />
                      </button>
                    )}
                  </ShareDocDialog>
                )}
              </Link>
            ))}
          </div>
        </section>
      ))}

      {pages.length === 0 && (
        <div className="py-12 text-center text-sm text-ev-text-muted">
          {hasActiveFilters ? "No documents match your search." : "No documents available."}
        </div>
      )}
    </div>
  );
}
